import { useCallback, useContext, useMemo } from 'react';
import api from 'api';
import { DataRoomNodeIdWithStateAndSignal, Lead } from 'models/Lead';
import LeadsContext from '../providers/Leads';
import ListingContext from '../providers/Listing/Listing.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ReleaseFileLoadingContext from '../providers/ReleaseFileLoading';
import {
  getIsNodeAtDownload,
  getIsNodeUnshared,
  getUpdatedAccessLevel,
} from '../components/LeadsTableContainer/utils';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';
import {
  confirmRemoveLeadsFromAutomation,
  confirmStopLeadsOrder,
} from 'modules/releaseOrderConfirm';

type ReleaseFileForLeadArgs = {
  leadId: string;
  dataRoomMemberId?: string;
  dataRoomNodeId: string;
  name: string;
  // The chip's current access, which tells whether the click shares or takes away the file.
  node: Partial<DataRoomNodeIdWithStateAndSignal>;
  onSuccessRelease?: (lead: Lead) => void;
};

export default function useReleaseFileForLead() {
  const { popServerError } = useContext(PopNotificationsContext);
  const { showReleaseFileUndo } = useContext(LeadsContext);
  const { dealRoom } = useContext(ListingContext);
  const { setReleaseFileLoadingForLead } = useContext(
    ReleaseFileLoadingContext,
  );

  const { getCopies } = useLeadStagesCopy();

  const stageCopies = useMemo(
    () => getCopies(dealRoom?.leadStagesCopies),
    [dealRoom?.leadStagesCopies, getCopies],
  );

  return useCallback(
    async ({
      leadId,
      dataRoomMemberId,
      dataRoomNodeId,
      name,
      node,
      onSuccessRelease,
    }: ReleaseFileForLeadArgs) => {
      setReleaseFileLoadingForLead(leadId, dataRoomNodeId, true);

      try {
        const sharesFile = getIsNodeUnshared(node);
        const revokesFile = getIsNodeAtDownload(node);

        // Sharing can skip the lead's order and taking access away can stop it, so both need a yes first.
        if (dataRoomMemberId && (sharesFile || revokesFile)) {
          const { data: preview } =
            await api.dataRoom.nodes.previewReleaseOrderShare(
              [dataRoomNodeId],
              {
                changedPermissions: [
                  { dataRoomMemberId, enableView: sharesFile },
                ],
              },
            );
          const { leads, leadsCount, step } = preview;

          if (
            leadsCount &&
            !(await confirmRemoveLeadsFromAutomation({
              leadNames: leads.map(({ name }) => name),
              leadsCount,
              filesCount: 1,
              stage: step && stageCopies[step],
            }))
          )
            return;

          if (!(await confirmStopLeadsOrder(preview, stageCopies))) return;
        }

        const { data } = await api.portfolio.seller.releaseFileForLead(
          leadId,
          dataRoomNodeId,
        );

        onSuccessRelease?.(data);
        showReleaseFileUndo?.({
          leadId,
          dataRoomNodeId,
          name,
          accessLevel: getUpdatedAccessLevel(data, dataRoomNodeId),
        });
      } catch (e) {
        popServerError(e);
      } finally {
        setReleaseFileLoadingForLead(leadId, dataRoomNodeId, false);
      }
    },
    [
      stageCopies,
      popServerError,
      setReleaseFileLoadingForLead,
      showReleaseFileUndo,
    ],
  );
}
